import React, { useState, useEffect, Children, useRef, ReactElement, FC } from 'react';
import styled from 'styled-components';
import { arrayUniqueByKey } from '../../../../utils/listHelpers';
import { logger } from '../../../../utils/logger';
import { useResize } from '../../../Layout';
import { DataTableDropdown, DataTableDropdownAction } from '../DataTableRowActions';
import debounce from 'lodash-es/debounce';
import { useDeepCompare } from '../../../../hooks/useCompare';

interface UseBulkActionsOverflowMenuInput {
  hideActions: boolean;
  children: Array<ReactElement | null | undefined> | ReactElement | null | undefined;
}

interface UseBulkActionsOverflowMenuOutput {
  overflowMenuWithActions: JSX.Element | null;
  bulkActions: JSX.Element[];
  isResizing: boolean;
}

const HEIGHT_OF_BUTTON_WITH_WRAPPED_TEXT = 50;

export const useBulkActionsOverflowMenu = ({
  children,
  hideActions
}: UseBulkActionsOverflowMenuInput): UseBulkActionsOverflowMenuOutput => {
  const actionsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const [initialChildren, setInitialChildren] = useState<JSX.Element[]>([]);
  const [childrenWithRef, setChildrenWithRef] = useState<{ bulkActions: JSX.Element[]; overflowMenu: JSX.Element[] }>({
    bulkActions: [],
    overflowMenu: []
  });
  const [isResizing, setIsResizing] = useState(false);
  const { dimensions } = useResize();

  const debounceResizingDone = debounce(() => setIsResizing(false), 300);
  const dimensionsHasChanged = useDeepCompare(dimensions);
  useEffect(() => {
    if (dimensionsHasChanged) {
      setIsResizing(true);
    }
    if (hideActions) {
      // clear all elements when BulkActions are hidden
      setInitialChildren([]);
      return;
    }
    const localChildrenWithRef: JSX.Element[] = [];
    try {
      const childrenToMap: JSX.Element =
        // Check if elements in DataTableBulkActions are wrapped with React.Fragment and choose children to map
        (
          !Array.isArray(children) && children?.type?.toString() === 'Symbol(react.fragment)'
            ? children.props.children
            : // Ensure that children is a list for the next transformations
              !Array.isArray(children)
              ? [children]
              : children
        ).filter((exist: JSX.Element) => !!exist);

      Children.forEach(childrenToMap, (Child, index) => {
        const key = index.toString();
        const childWithRef = (
          <div
            className="action-with-ref"
            // Set reference for all child elements
            ref={(ref) => {
              actionsRef.current[key] = ref;
            }}
            key={key}
          >
            {Child}
          </div>
        );
        localChildrenWithRef.push(childWithRef);
      });
      setInitialChildren(localChildrenWithRef);
    } catch (e) {
      logger.warn(`
        Please ensure that structure of children in <DataTableBulkActions /> is correct.
        Actions should be direct children of <DataTableBulkActions />. Only 1 extra <React.Fragment /> is allowed.
        This warning means that auto rwd mode for actions will not work
        Full error message: 
        ${e}
      `);
      setInitialChildren(children as JSX.Element[]);
    }
  }, [hideActions, dimensionsHasChanged, children]);

  const childrenHasChanged = useDeepCompare(initialChildren);
  const childrenHeights = JSON.stringify(Object.values(actionsRef.current).map((el) => el?.clientHeight));

  // biome-ignore lint/correctness/useExhaustiveDependencies: exclude debounceResizingDone to avoid infinite loop
  useEffect(() => {
    if (!hideActions && initialChildren.length >= 1) {
      if (dimensionsHasChanged || initialChildren.length !== childrenWithRef.bulkActions.length) {
        setIsResizing(true);
      }
      setChildrenWithRef((prevState) => {
        if (dimensionsHasChanged || childrenHasChanged) {
          return {
            bulkActions: initialChildren,
            overflowMenu: []
          };
        }
        const lastChildren = prevState.bulkActions
          .slice()
          .reverse()
          .find((el) => !!el);
        const lastItemKey = lastChildren?.key;

        // verify if the last element has wrapped text
        const lastItem = actionsRef.current[lastItemKey ?? ''];
        const lastButtonHasWrappedText = !!lastItem && lastItem.clientHeight > HEIGHT_OF_BUTTON_WITH_WRAPPED_TEXT;

        // Verify if at least 1 element has wrapped text
        const atLeastOnButtonHasWrappedText = prevState.bulkActions.some((el) => {
          const bulkAction = actionsRef.current[el.key ?? ''];
          return !bulkAction?.clientHeight ? false : bulkAction?.clientHeight > HEIGHT_OF_BUTTON_WITH_WRAPPED_TEXT;
        });

        // control elements which should be moved to the overflow menu
        if (!!lastItemKey && (lastButtonHasWrappedText || atLeastOnButtonHasWrappedText)) {
          return {
            bulkActions: prevState.bulkActions.filter((el) => el.key !== lastItemKey),
            overflowMenu: arrayUniqueByKey([lastChildren, ...prevState.overflowMenu], 'key')
          };
        }
        return {
          bulkActions: prevState.bulkActions,
          overflowMenu: prevState.overflowMenu.filter((menuItem) => menuItem.key !== lastItemKey)
        };
      });
      // reset resizing state
      debounceResizingDone();
    }
  }, [
    hideActions,
    childrenWithRef.bulkActions,
    dimensionsHasChanged,
    initialChildren,
    childrenHasChanged,
    childrenHeights
  ]);

  const overflowMenuWithActions =
    childrenWithRef.overflowMenu?.length > 0 ? (
      <OverflowActions childrenForOverflowMenu={childrenWithRef.overflowMenu} />
    ) : null;
  return { overflowMenuWithActions, bulkActions: childrenWithRef.bulkActions, isResizing };
};

const OverflowActions: FC<React.PropsWithChildren<{ childrenForOverflowMenu: ReactElement[] }>> = ({
  childrenForOverflowMenu
}): ReactElement => (
  <DataTableDropdown actionsLabel="Bulk actions">
    {childrenForOverflowMenu.map((menuItem, index) => (
      <StyledDataTableDropdownActionWrapper key={index}>
        <DataTableDropdownAction>{menuItem}</DataTableDropdownAction>
      </StyledDataTableDropdownActionWrapper>
    ))}
  </DataTableDropdown>
);

// This hook take elements from bulk actions and try to style it similar to DropdownActions
const StyledDataTableDropdownActionWrapper = styled.div`
  .MuiListItem-button {
    padding: 0;
  }
  .action-with-ref {
    width: 100%;
    > span,
    .bulk-action-separator + span {
      width: 100%;
    }
    .MuiButtonBase-root {
      padding: 6px 16px;
      border: none;
      border-radius: none;
      width: 100%;
      justify-content: flex-start;
      &:hover {
        background: none;
      }
    }
    .bulk-action-separator {
      display: none;
    }
  }
`;
